<template>
  <div class="chatbot-wrapper">
    <!-- Panneau de conversation -->
    <transition name="chatbot-slide">
      <div v-if="open" class="chatbot-panel" :class="{ 'chatbot-panel--expanded': expanded }">
        <div class="chatbot-header">
          <div class="chatbot-header-left">
            <div class="chatbot-pulse-dot"></div>
            <div>
              <div class="chatbot-title">FleetAgent</div>
              <div class="chatbot-subtitle">Analyse intelligente de flotte</div>
            </div>
          </div>
          <div class="chatbot-header-actions">
            <v-btn icon x-small class="chatbot-action-btn" :disabled="messages.length === 0" title="Effacer la conversation" @click="clearMessages">
              <v-icon small>mdi-delete-sweep-outline</v-icon>
            </v-btn>
            <v-btn icon x-small class="chatbot-action-btn" :title="expanded ? 'Réduire' : 'Agrandir'" @click="expanded = !expanded">
              <v-icon small>{{ expanded ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
            </v-btn>
            <v-btn icon x-small class="chatbot-action-btn" title="Fermer" @click="open = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="chatbot-messages" ref="messageList">
          <div v-if="messages.length === 0" class="chatbot-empty">
            <v-icon size="40" color="rgba(255,255,255,0.3)">mdi-robot-outline</v-icon>
            <p>Posez une question sur votre flotte&nbsp;: TCO, renouvellements, consommation&hellip;</p>
          </div>

          <div
            v-for="(msg, i) in messages"
            :key="i"
            class="chatbot-message"
            :class="msg.role === 'user' ? 'chatbot-message--user' : 'chatbot-message--agent'"
          >
            <div class="chatbot-bubble">
              <div class="chatbot-markdown" v-html="renderMarkdown(msg.text)"></div>
            </div>

            <!-- Détail des requêtes SQL exécutées -->
            <v-expansion-panels
              v-if="msg.role === 'agent' && msg.queries && msg.queries.length"
              flat
              class="chatbot-queries"
            >
              <v-expansion-panel class="chatbot-queries-panel">
                <v-expansion-panel-header class="chatbot-queries-header">
                  <span>
                    <v-icon x-small class="mr-1">mdi-database-search</v-icon>
                    {{ msg.queries.length }} requête{{ msg.queries.length > 1 ? 's' : '' }} exécutée{{ msg.queries.length > 1 ? 's' : '' }}
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(q, qi) in msg.queries" :key="qi" class="chatbot-query-item">
                    <code class="chatbot-query-sql">{{ q.input && q.input.sql ? q.input.sql : q.tool }}</code>
                    <span v-if="q.output && q.output.row_count !== undefined" class="chatbot-query-count">
                      {{ q.output.row_count }} ligne{{ q.output.row_count > 1 ? 's' : '' }}
                    </span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <!-- Indicateur de saisie -->
          <div v-if="loading" class="chatbot-message chatbot-message--agent">
            <div class="chatbot-bubble chatbot-bubble--typing">
              <span class="typing-dot"></span>
              <span class="typing-dot"></span>
              <span class="typing-dot"></span>
            </div>
          </div>
        </div>

        <div class="chatbot-input-area">
          <v-text-field
            v-model="input"
            placeholder="Posez votre question…"
            solo
            flat
            hide-details
            dense
            class="chatbot-input"
            :disabled="loading"
            @keydown.enter.exact.prevent="sendMessage"
          />
          <v-btn
            icon
            :disabled="loading || !input.trim()"
            color="primary"
            class="chatbot-send-btn"
            @click="sendMessage"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </div>
      </div>
    </transition>

    <!-- Bouton FAB -->
    <v-btn
      fab
      :color="open ? 'grey darken-3' : 'primary'"
      class="chatbot-fab"
      elevation="8"
      @click="open = !open"
    >
      <v-icon>{{ open ? 'mdi-close' : 'mdi-robot' }}</v-icon>
    </v-btn>
  </div>
</template>

<script>
import { marked } from 'marked'

marked.setOptions({ breaks: true })

export default {
  name: 'AFleetChatbot',
  data() {
    return {
      open: false,
      expanded: false,
      input: '',
      loading: false,
      messages: []
    }
  },
  methods: {
    async sendMessage() {
      const text = this.input.trim()
      if (!text || this.loading) return

      this.messages.push({ role: 'user', text })
      this.input = ''
      this.loading = true
      this.$nextTick(this.scrollToBottom)

      try {
        const response = await this.$http.post('fleet/ask', { message: text }, { timeout: 180000 })
        this.messages.push({
          role: 'agent',
          text: response.data.text || 'Pas de réponse.',
          queries: response.data.queries_executed || []
        })
      } catch (err) {
        let errMsg
        if (err.code === 'ECONNABORTED') {
          errMsg = "La requête a expiré (>3 min). Essayez une question plus précise ou réduisez la portée de l'analyse."
        } else if (err.response && err.response.data && err.response.data.error) {
          errMsg = err.response.data.error
        } else {
          errMsg = 'Une erreur est survenue. Veuillez réessayer.'
        }
        this.messages.push({ role: 'agent', text: errMsg, queries: [] })
      } finally {
        this.loading = false
        this.$nextTick(this.scrollToBottom)
      }
    },
    clearMessages() {
      this.messages = []
    },
    scrollToBottom() {
      const el = this.$refs.messageList
      if (el) el.scrollTop = el.scrollHeight
    },
    renderMarkdown(text) {
      if (!text) return ''
      return marked.parse(text)
    }
  }
}
</script>

<style scoped>
/* ── Conteneur racine ── */
.chatbot-wrapper {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

/* ── FAB ── */
.chatbot-fab {
  flex-shrink: 0;
}

/* ── Panneau principal ── */
.chatbot-panel {
  width: 400px;
  height: 540px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background: linear-gradient(135deg, rgba(18, 22, 36, 0.82), rgba(30, 36, 54, 0.82));
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(20px) saturate(150%);
  -webkit-backdrop-filter: blur(20px) saturate(150%);
  color: #f0f4ff;
  transition: width 0.35s cubic-bezier(0.34, 1.26, 0.64, 1),
              height 0.35s cubic-bezier(0.34, 1.26, 0.64, 1),
              top 0.35s cubic-bezier(0.34, 1.26, 0.64, 1),
              left 0.35s cubic-bezier(0.34, 1.26, 0.64, 1),
              right 0.35s cubic-bezier(0.34, 1.26, 0.64, 1),
              bottom 0.35s cubic-bezier(0.34, 1.26, 0.64, 1),
              border-radius 0.35s ease;
}

.chatbot-panel--expanded {
  position: fixed;
  top: 24px;
  left: 24px;
  right: 24px;
  bottom: 80px;
  width: auto;
  height: auto;
  border-radius: 24px;
}

.chatbot-panel::before {
  content: '';
  position: absolute;
  inset: -60%;
  pointer-events: none;
  z-index: 0;
  background:
    radial-gradient(40% 40% at 25% 30%, rgba(94, 234, 212, 0.3), transparent 70%),
    radial-gradient(35% 35% at 75% 20%, rgba(129, 140, 248, 0.25), transparent 70%),
    radial-gradient(45% 45% at 60% 75%, rgba(56, 189, 248, 0.2), transparent 75%);
  filter: blur(24px);
  opacity: 0.7;
  animation: chatbot-drift 14s ease-in-out infinite;
}

.chatbot-panel > * {
  position: relative;
  z-index: 1;
}

/* ── En-tête ── */
.chatbot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
}

.chatbot-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chatbot-header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.chatbot-action-btn {
  color: rgba(255, 255, 255, 0.55) !important;
  transition: color 0.15s ease;
}
.chatbot-action-btn:hover {
  color: rgba(255, 255, 255, 0.9) !important;
}

.chatbot-pulse-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.9);
  animation: chatbot-pulse 1.8s infinite;
  flex-shrink: 0;
}

.chatbot-title {
  font-weight: 700;
  font-size: 15px;
  line-height: 1.1;
}

.chatbot-subtitle {
  font-size: 11px;
  opacity: 0.6;
  font-weight: 400;
}

/* ── Zone messages ── */
.chatbot-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 6px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scroll-behavior: smooth;
}

.chatbot-messages::-webkit-scrollbar { width: 4px; }
.chatbot-messages::-webkit-scrollbar-track { background: transparent; }
.chatbot-messages::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.chatbot-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  opacity: 0.5;
  font-size: 13px;
  padding: 20px;
}

/* ── Bulles ── */
.chatbot-message {
  display: flex;
  flex-direction: column;
  max-width: 88%;
}

.chatbot-message--user {
  align-self: flex-end;
  align-items: flex-end;
}

.chatbot-message--agent {
  align-self: flex-start;
  align-items: flex-start;
}

.chatbot-bubble {
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 13.5px;
  line-height: 1.55;
  word-break: break-word;
}

.chatbot-message--user .chatbot-bubble {
  background: rgba(99, 102, 241, 0.75);
  border-bottom-right-radius: 4px;
}

.chatbot-message--agent .chatbot-bubble {
  background: rgba(255, 255, 255, 0.08);
  border-bottom-left-radius: 4px;
}

/* ── Markdown rendu ── */
.chatbot-markdown >>> p {
  margin: 0 0 6px;
}
.chatbot-markdown >>> p:last-child {
  margin-bottom: 0;
}
.chatbot-markdown >>> h1,
.chatbot-markdown >>> h2,
.chatbot-markdown >>> h3 {
  margin: 10px 0 4px;
  font-weight: 700;
  line-height: 1.2;
}
.chatbot-markdown >>> h1 { font-size: 16px; }
.chatbot-markdown >>> h2 { font-size: 14.5px; }
.chatbot-markdown >>> h3 { font-size: 13.5px; }
.chatbot-markdown >>> ul,
.chatbot-markdown >>> ol {
  margin: 4px 0 6px;
  padding-left: 18px;
}
.chatbot-markdown >>> li {
  margin-bottom: 2px;
}
.chatbot-markdown >>> strong { font-weight: 700; }
.chatbot-markdown >>> em { font-style: italic; opacity: 0.85; }
.chatbot-markdown >>> code {
  background: rgba(0, 0, 0, 0.35);
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 12px;
  color: #7dd3fc;
  font-family: 'Courier New', monospace;
}
.chatbot-markdown >>> pre {
  background: rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  padding: 10px 12px;
  overflow-x: auto;
  margin: 6px 0;
}
.chatbot-markdown >>> pre code {
  background: transparent;
  padding: 0;
  font-size: 11.5px;
  color: #7dd3fc;
}
.chatbot-markdown >>> blockquote {
  border-left: 3px solid rgba(129, 140, 248, 0.6);
  padding-left: 10px;
  margin: 6px 0;
  opacity: 0.8;
}
.chatbot-markdown >>> hr {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 8px 0;
}
.chatbot-markdown >>> table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin: 6px 0;
}
.chatbot-markdown >>> th,
.chatbot-markdown >>> td {
  padding: 5px 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: left;
}
.chatbot-markdown >>> th {
  background: rgba(129, 140, 248, 0.15);
  font-weight: 600;
}

/* Indicateur typing */
.chatbot-bubble--typing {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 12px 16px;
}

.typing-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  animation: chatbot-typing 1.2s ease-in-out infinite;
}
.typing-dot:nth-child(2) { animation-delay: 0.2s; }
.typing-dot:nth-child(3) { animation-delay: 0.4s; }

/* ── Requêtes SQL ── */
.chatbot-queries {
  margin-top: 4px;
  background: transparent !important;
  max-width: 100%;
}

.chatbot-queries-panel {
  background: rgba(255, 255, 255, 0.04) !important;
  border-radius: 10px !important;
  border: 1px solid rgba(255, 255, 255, 0.07) !important;
}

.chatbot-queries-header {
  font-size: 11px !important;
  color: rgba(255, 255, 255, 0.5) !important;
  min-height: 32px !important;
  padding: 0 10px !important;
}

.chatbot-query-item { margin-bottom: 8px; }

.chatbot-query-sql {
  display: block;
  font-size: 10.5px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  padding: 6px 8px;
  color: #7dd3fc;
  white-space: pre-wrap;
  word-break: break-all;
}

.chatbot-query-count {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 2px;
  display: block;
}

/* ── Zone de saisie ── */
.chatbot-input-area {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  gap: 6px;
  flex-shrink: 0;
}

.chatbot-input { flex: 1; font-size: 13.5px; }

.chatbot-input >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.07) !important;
  border-radius: 12px !important;
  padding: 0 12px !important;
  min-height: 40px !important;
}

.chatbot-input >>> input::placeholder { color: rgba(255, 255, 255, 0.35) !important; }
.chatbot-input >>> input { color: #f0f4ff !important; caret-color: #818cf8 !important; }

.chatbot-send-btn { flex-shrink: 0; }

/* ── Transitions ── */
.chatbot-slide-enter-active,
.chatbot-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;
}

.chatbot-slide-enter,
.chatbot-slide-leave-to {
  transform: translateY(24px) scale(0.96);
  opacity: 0;
}

/* ── Keyframes ── */
@keyframes chatbot-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.8); }
  70%  { box-shadow: 0 0 0 8px rgba(74, 222, 128, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
}

@keyframes chatbot-typing {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
  30%            { transform: translateY(-5px); opacity: 1; }
}

@keyframes chatbot-drift {
  0%   { transform: translate3d(-3%, 2%, 0) scale(1.05) rotate(0deg); }
  50%  { transform: translate3d(3%, -2%, 0) scale(1.12) rotate(5deg); }
  100% { transform: translate3d(-3%, 2%, 0) scale(1.05) rotate(0deg); }
}
</style>
